import { getCurrencySymbol, TitleCasePipe, CurrencyPipe } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Inject, Input, LOCALE_ID } from '@angular/core';
import { RouterLink } from '@angular/router';

import type { BillingEstimate, TaxesEstimate } from '@core/billing-estimate-types';
import { getProductCodename } from '@core/billing-plans-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { BillingPlan, BillingPlanTier, Price } from '@core/products-types';
import type { Addon, PaymentInterval } from '@core/subscription-types';
import {
  IconHelpComponent,
  DividerComponent,
  ButtonComponent,
  RtTooltip,
  IconComponent,
  BoxComponent,
} from '@core/user-interface-legacy';

import { formatPrice } from '../../../helpers/format-price';
import { get2024ProductCosts } from '../../../helpers/get-2024-product-costs';
import { getProductCost } from '../../../helpers/get-product-cost';
import { isPp24 } from '../../../helpers/is-pp24';
import { ProductNamePipe } from '../../../shared/product-name/product-name.pipe';

@Component({
  selector: 'rt-product-order-summary',
  templateUrl: './product-order-summary.component.html',
  styleUrls: ['./product-order-summary.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    IconComponent,
    RtTooltip,
    ButtonComponent,
    RouterLink,
    DividerComponent,
    IconHelpComponent,
    TitleCasePipe,
    CurrencyPipe,
    TranslatePipe,
    ProductNamePipe,
  ],
})
export class ProductOrderSummaryComponent {
  @Input() public product: BillingPlan;
  @Input() public price: Price | undefined;
  @Input() public slots: number | undefined;
  @Input() public paymentInterval: PaymentInterval;
  @Input() public companyName: string;
  @Input() public billingEstimate?: BillingEstimate;
  @Input() public billingCountry?: string;
  @Input() public addons: Addon[];

  public estimatedCostWithTaxes?: number;
  public humanReadableSummaryInterval: string;
  public humanReadableDetailsInterval: string;
  public taxInformation: string = '';
  public taxTooltip: string = '';

  public currencySymbol: string;
  public currencyCode: string;
  public totalCost: number;

  constructor(
    private i18n: I18nService,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (this.product) {
      const slots =
        this.product && this.slots && this.slots > this.product.baseSlots
          ? this.slots
          : this.product.baseSlots;

      if (isPp24(this.product.tier) && this.price) {
        this.totalCost = get2024ProductCosts(
          this.product.prices,
          this.addons,
          this.price.fteRangeStart,
          this.paymentInterval,
        );
      } else {
        this.totalCost = getProductCost(
          this.product,
          slots,
          this.paymentInterval,
          this.addons,
          this.price,
        );
      }

      this.currencySymbol = getCurrencySymbol(this.product.currency, 'narrow');
      this.currencyCode = getCurrencySymbol(this.product.currency, 'wide');

      this.humanReadableSummaryInterval = this.getHumanReadableSummaryInterval();

      this.humanReadableDetailsInterval = this.getHumanReadableDetailsInterval();
    }
    if (changes.billingEstimate) {
      this.setPaymentEstimate();
      this.setTaxInformation();
      this.humanReadableSummaryInterval = this.getHumanReadableSummaryInterval();
    }
  }

  public get codename(): BillingPlanTier {
    return getProductCodename(this.product);
  }

  public get showTaxInfo(): boolean {
    return this.billingEstimate ? !!this.estimatedCostWithTaxes : true;
  }

  public getHumanReadableDetailsInterval() {
    const remainder = +this.paymentInterval % 12;
    const numberOfYears = +this.paymentInterval / 12;
    const isXYearInterval = numberOfYears > 1 && remainder === 0;

    switch (this.paymentInterval.toString()) {
      case 'month':
      case '1':
        return this.i18n.translate('app.pricing.product_order.monthly_payment');

      case 'year':
      case '12':
        return this.i18n.translate('app.pricing.product_order.annual_payment');

      default:
        return isXYearInterval
          ? this.i18n.translate('app.pricing.product_order.x_year_payment', {
              interval: numberOfYears,
            })
          : this.i18n.translate('app.pricing.product_order.x_month_payment', {
              interval: this.paymentInterval,
            });
    }
  }

  public getHumanReadableSummaryInterval() {
    if (this.billingEstimate) {
      return this.i18n.translate('app.pricing.product_order.amount_due_today');
    }

    const remainder = +this.paymentInterval % 12;
    const numberOfYears = +this.paymentInterval / 12;
    const isXYearInterval = numberOfYears > 1 && remainder === 0;

    switch (this.paymentInterval.toString()) {
      case 'month':
      case '1':
        return this.i18n.translate('app.pricing.product_order.monthly_cost');

      case 'year':
      case '12':
        return this.i18n.translate('app.pricing.product_order.annual_cost');

      default:
        return isXYearInterval
          ? this.i18n.translate('app.pricing.product_order.x_year_cost', {
              interval: numberOfYears,
            })
          : this.i18n.translate('app.pricing.product_order.x_month_cost', {
              interval: this.paymentInterval,
            });
    }
  }

  private setTaxInformation() {
    if (!this.billingEstimate) {
      this.taxInformation = '';
      this.taxTooltip = '';
      return;
    }
    const { taxes } = this.billingEstimate;

    this.taxInformation = this.getTaxesDetails(taxes);
    this.taxTooltip = this.getTaxTooltip();
  }

  private getTaxesDetails(taxes: TaxesEstimate[]) {
    const taxesApplied = !!taxes.length;
    if (!taxesApplied) {
      return this.i18n.translate(
        this.isUS()
          ? 'app.pricing.product_order.no_sales_tax'
          : 'app.pricing.product_order.vat_reverse_charge',
      );
    }

    const vat = taxes.find(tax => tax.name === 'VAT');
    const amount = this.getFormattedTaxAmount(taxes);

    if (vat) {
      const percent = vat.description.replaceAll(/\D/g, '');
      return this.i18n.translate('app.pricing.product_order.vat_included', {
        rate: `${percent}%`,
        amount,
      });
    } else {
      return this.i18n.translate('app.pricing.product_order.sales_tax_included', {
        amount,
      });
    }
  }

  private getTaxTooltip() {
    if (this.isUS()) {
      return this.i18n.translate('app.pricing.product_order.taxes_based_on_address');
    }

    if (this.isVatReverseCharge()) {
      return this.i18n.translate('app.pricing.product_order.vat_reverse_charge_tooltip');
    }

    return this.i18n.translate('app.pricing.product_order.vat_based_on_location');
  }

  private setPaymentEstimate() {
    if (!this.billingEstimate) {
      this.estimatedCostWithTaxes = undefined;
      return;
    }
    const { amountDue } = this.billingEstimate;
    this.estimatedCostWithTaxes = (amountDue || 0) / 100;
  }

  private getFormattedTaxAmount(taxes: TaxesEstimate[]) {
    const amount = taxes.reduce((acc, tax) => acc + tax.amount, 0);
    return formatPrice(amount / 100, this.locale, this.product.currency);
  }

  private isVatReverseCharge(): boolean {
    return !!this.billingEstimate?.lineItems.find(
      item => item.taxExemptReason === 'reverse_charge',
    );
  }

  private isUS(): boolean {
    return this.billingCountry === 'US';
  }
}
